import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, FileText, AlertTriangle, Upload, X } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { reports } from '../utils/api';
import Logo from '../components/Logo';

const ReportForm = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const justPaid = searchParams.get('payment') === 'success';

  // After Stripe / voucher checkout, refresh local user state so the
  // newly-granted credit count is visible before the no-credits guard runs.
  useEffect(() => {
    if (justPaid) refreshUser();
  }, [justPaid, refreshUser]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState(1);
  const [voiceDnaFile, setVoiceDnaFile] = useState(null);
  const [voiceDnaContent, setVoiceDnaContent] = useState('');

  const [formData, setFormData] = useState({
    companyName: '',
    individualName: '',
    registrationNumber: '',
    aliases: '',
    dateOfBirth: '',
    nationality: '',
    transactionType: 'partnership_business',
    transactionDetails: {
      commodityType: '',
      sourceCountry: '',
      destinationCountry: '',
      transitJurisdictions: '',
      transactionValue: '',
      frequency: 'one-off',
      positionRole: '',
      accessLevel: 'standard'
    },
    subjectJurisdictions: '',
    clientJurisdiction: 'United Kingdom',
    regulatoryRestrictions: '',
    existingRedFlags: '',
    previousDealings: '',
    specificRisks: '',
    identifiers: {
      websiteUrls: '',
      emailDomains: '',
      physicalAddresses: '',
      socialMedia: '',
      licenseNumbers: ''
    },
    additionalContext: '', // Optional high-level detail about report purpose
    disclaimerAccepted: false
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: { ...prev[parent], [child]: value }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleVoiceDnaUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.name.endsWith('.md') && !file.name.endsWith('.txt')) {
      setError('Character DNA file must be a .md or .txt file');
      return;
    }
    if (file.size > 100000) {
      setError('Character DNA file must be under 100KB');
      return;
    }
    const reader = new FileReader();
    reader.onload = (evt) => {
      setVoiceDnaFile(file.name);
      setVoiceDnaContent(evt.target.result);
      setError('');
    };
    reader.readAsText(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.disclaimerAccepted) {
      setError('You must accept the disclaimer to proceed.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const result = await reports.create({
        ...formData,
        disclaimerAccepted: String(formData.disclaimerAccepted),
        voiceDna: voiceDnaContent || null
      });
      await refreshUser();
      // Navigate to reports list with success message
      navigate('/reports', {
        state: {
          message: `Report ${result.reportId} is being generated. You'll receive an email when it's ready.`,
          reportId: result.reportId
        }
      });
    } catch (err) {
      setError(err.message || 'Failed to create report');
    } finally {
      setLoading(false);
    }
  };

  const transactionTypes = [
    { value: 'commodity', label: 'Commodity Trade' },
    { value: 'investment_seeking', label: 'Seeking Investment, Loan or Financial Arrangement' },
    { value: 'investment_making', label: 'Making Investment, Loan or Financial Arrangement' },
    { value: 'employment_employee', label: 'Employment — Potential Employee' },
    { value: 'employment_employer', label: 'Employment — Potential Employer' },
    { value: 'partnership_individual', label: 'Potential Business Partner (Individual)' },
    { value: 'partnership_business', label: 'Potential Business Collaborator (Business)' },
    { value: 'technology_buying', label: 'Technology/IP — Buying IP or Technology From' },
    { value: 'technology_licensing_from', label: 'Technology/IP — Licensing IP or Technology From' },
    { value: 'technology_selling', label: 'Technology/IP — Selling IP or Technology To' },
    { value: 'technology_licensing_to', label: 'Technology/IP — Licensing IP or Technology To' },
    { value: 'technology_crosslicense', label: 'Technology/IP — Cross-Licensing IP/Technology' },
    { value: 'vendor', label: 'Vendor / Supplier Engagement' },
    { value: 'other', label: 'Other' }
  ];

  const commoditySubTypes = [
    { value: '', label: 'Select commodity category (optional)' },
    { value: 'food_agriculture', label: 'Food & Agriculture' },
    { value: 'metals', label: 'Metals' },
    { value: 'critical_metals', label: 'Critical Metals / Rare Earth' },
    { value: 'export_licence', label: 'Export Licence Required Product or Material' },
    { value: 'energy', label: 'Energy (Oil, Gas, Coal)' },
    { value: 'minerals', label: 'Minerals & Mining' },
    { value: 'other_commodity', label: 'Other Commodity' }
  ];

  const licenceTypes = [
    { value: '', label: 'Select licence type (optional)' },
    { value: 'exclusive', label: 'Exclusive Licence' },
    { value: 'non_exclusive', label: 'Non-Exclusive Licence' },
    { value: 'phantom', label: 'Phantom Licence' },
    { value: 'other_licence', label: 'Other' }
  ];

  const isCommodity = formData.transactionType === 'commodity';
  const isLicensing = ['technology_licensing_from', 'technology_licensing_to', 'technology_crosslicense'].includes(formData.transactionType);

  // Suppress the "no credits" bounce while a payment-success refresh is in
  // flight — otherwise the user would see this for a split second before
  // refreshUser() updates the React state, then bounce to /pricing.
  if (user?.subscription?.reportsRemaining <= 0 && !justPaid) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-lg text-center max-w-md">
          <AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">No Report Credits</h2>
          <p className="text-gray-600 mb-4">You need to purchase a plan to generate reports.</p>
          <Link to="/pricing" className="bg-red-600 text-white px-6 py-2 rounded-md inline-block">
            View Plans
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
            <ArrowLeft className="h-5 w-5" />
            Back
          </Link>
          <Logo size="sm" />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h1 className="text-2xl font-bold mb-6 flex items-center gap-2">
            <FileText className="h-6 w-6 text-red-600" />
            New Due Diligence Report
          </h1>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
              <p className="text-red-600">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Section 1: Subject Identification */}
            <section>
              <h2 className="text-lg font-semibold mb-4 pb-2 border-b">1. Subject Identification</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Company Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="companyName"
                    required
                    value={formData.companyName}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border rounded-md focus:ring-red-500 focus:border-red-500"
                    placeholder="e.g., Acme Corporation Ltd"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Individual Name</label>
                  <input
                    type="text"
                    name="individualName"
                    value={formData.individualName}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border rounded-md"
                    placeholder="e.g., John Smith"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Registration Number</label>
                  <input
                    type="text"
                    name="registrationNumber"
                    value={formData.registrationNumber}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border rounded-md"
                    placeholder="e.g., 12345678"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Known Aliases</label>
                  <input
                    type="text"
                    name="aliases"
                    value={formData.aliases}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border rounded-md"
                    placeholder="Other trading names"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nationality</label>
                  <input
                    type="text"
                    name="nationality"
                    value={formData.nationality}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border rounded-md"
                    placeholder="e.g., British"
                  />
                </div>
              </div>
            </section>

            {/* Section 2: Transaction Details */}
            <section>
              <h2 className="text-lg font-semibold mb-4 pb-2 border-b">2. Transaction Details</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Transaction Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="transactionType"
                    value={formData.transactionType}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    {transactionTypes.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Additional Context</label>
                  <textarea
                    name="additionalContext"
                    value={formData.additionalContext}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border rounded-md"
                    rows={2}
                    placeholder="e.g. research project, mining project, wish to understand any risks of association"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Optional — even a few words of additional detail regarding the purpose of the report can lead to a more useful result.
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Transaction Value</label>
                  <input
                    type="text"
                    name="transactionDetails.transactionValue"
                    value={formData.transactionDetails.transactionValue}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border rounded-md"
                    placeholder="e.g., £500,000"
                  />
                </div>
                {/* Commodity-specific follow-ups */}
                {isCommodity && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Are commodities being bought or sold?</label>
                      <select
                        name="transactionDetails.commodityDirection"
                        value={formData.transactionDetails.commodityDirection || ''}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border rounded-md"
                      >
                        <option value="">Prefer not to say</option>
                        <option value="buying">Buying / Importing</option>
                        <option value="selling">Selling / Exporting</option>
                        <option value="both">Both</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Commodity Category</label>
                      <select
                        name="transactionDetails.commodityCategory"
                        value={formData.transactionDetails.commodityCategory || ''}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border rounded-md"
                      >
                        {commoditySubTypes.map(t => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Commodity Type</label>
                      <input
                        type="text"
                        name="transactionDetails.commodityType"
                        value={formData.transactionDetails.commodityType}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border rounded-md"
                        placeholder="e.g., Coffee, Copper, Lithium"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Source Country</label>
                      <input
                        type="text"
                        name="transactionDetails.sourceCountry"
                        value={formData.transactionDetails.sourceCountry}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border rounded-md"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Destination Country</label>
                      <input
                        type="text"
                        name="transactionDetails.destinationCountry"
                        value={formData.transactionDetails.destinationCountry}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border rounded-md"
                      />
                    </div>
                  </>
                )}

                {/* Licensing follow-up */}
                {isLicensing && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Licence Type</label>
                    <select
                      name="transactionDetails.licenceType"
                      value={formData.transactionDetails.licenceType || ''}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border rounded-md"
                    >
                      {licenceTypes.map(t => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </section>

            {/* Section 3: Jurisdictional Context */}
            <section>
              <h2 className="text-lg font-semibold mb-4 pb-2 border-b">3. Jurisdictional Context</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subject's Operating Jurisdictions</label>
                  <input
                    type="text"
                    name="subjectJurisdictions"
                    value={formData.subjectJurisdictions}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border rounded-md"
                    placeholder="e.g., UK, UAE, Singapore"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Your Business Jurisdiction</label>
                  <input
                    type="text"
                    name="clientJurisdiction"
                    value={formData.clientJurisdiction}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border rounded-md"
                  />
                </div>
              </div>
            </section>

            {/* Section 4: Concerns & History */}
            <section>
              <h2 className="text-lg font-semibold mb-4 pb-2 border-b">4. Concerns & History</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Existing Red Flags</label>
                  <textarea
                    name="existingRedFlags"
                    value={formData.existingRedFlags}
                    onChange={handleChange}
                    rows={3}
                    className="w-full px-3 py-2 border rounded-md"
                    placeholder="Any concerns or issues you're already aware of"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Previous Dealings</label>
                  <textarea
                    name="previousDealings"
                    value={formData.previousDealings}
                    onChange={handleChange}
                    rows={2}
                    className="w-full px-3 py-2 border rounded-md"
                    placeholder="Any prior relationship or transactions"
                  />
                </div>
              </div>
            </section>

            {/* Section 5: Available Identifiers */}
            <section>
              <h2 className="text-lg font-semibold mb-4 pb-2 border-b">5. Available Identifiers</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Website URLs</label>
                  <input
                    type="text"
                    name="identifiers.websiteUrls"
                    value={formData.identifiers.websiteUrls}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border rounded-md"
                    placeholder="https://example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Social Media</label>
                  <input
                    type="text"
                    name="identifiers.socialMedia"
                    value={formData.identifiers.socialMedia}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border rounded-md"
                    placeholder="LinkedIn, Twitter profiles"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Physical Addresses</label>
                  <textarea
                    name="identifiers.physicalAddresses"
                    value={formData.identifiers.physicalAddresses}
                    onChange={handleChange}
                    rows={2}
                    className="w-full px-3 py-2 border rounded-md"
                    placeholder="Known business addresses"
                  />
                </div>
              </div>
            </section>

            {/* Character DNA — Writing Style */}
            <section className="bg-white border border-gray-200 rounded-md p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Report Writing Style (Optional)</h3>
              <p className="text-sm text-gray-600 mb-4">
                By default, reports are written in a formal professional style typical of law firms and consulting practices.
                If you have a Character DNA profile (.md file) that defines your preferred writing style, you can upload it here.
                The report structure and content remain the same — only the writing voice, tone, and vocabulary are adapted.
                If you do not have a Character DNA already you can create one, either for free or for $5 USD, at our partner site{' '}
                <a href="https://diversely.com" target="_blank" rel="noopener noreferrer" className="text-red-600 underline hover:text-red-700">https://diversely.com</a>
              </p>
              <div className="flex items-center gap-4">
                {!voiceDnaFile ? (
                  <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded-md text-sm text-gray-700 transition-colors">
                    <Upload className="h-4 w-4" />
                    Upload Character DNA (.md)
                    <input
                      type="file"
                      accept=".md,.txt"
                      onChange={handleVoiceDnaUpload}
                      className="hidden"
                    />
                  </label>
                ) : (
                  <div className="flex items-center gap-2 px-4 py-2 bg-green-50 border border-green-200 rounded-md text-sm text-green-800">
                    <FileText className="h-4 w-4" />
                    <span>{voiceDnaFile}</span>
                    <button
                      type="button"
                      onClick={() => { setVoiceDnaFile(null); setVoiceDnaContent(''); }}
                      className="ml-2 text-green-600 hover:text-red-600"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                )}
                <span className="text-xs text-gray-400">or leave blank for default professional style</span>
              </div>
            </section>

            {/* Disclaimer */}
            <section className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="disclaimerAccepted"
                  name="disclaimerAccepted"
                  checked={formData.disclaimerAccepted}
                  onChange={handleChange}
                  className="mt-1 h-4 w-4 text-red-600 border-gray-300 rounded"
                />
                <label htmlFor="disclaimerAccepted" className="text-sm text-gray-700">
                  <strong>I understand and accept</strong> that this report is provided for information only and is based on publicly available open-source information which may or may not be accurate, complete, or current. It does not constitute legal advice. I should conduct my own verification and seek professional legal advice where appropriate.
                </label>
              </div>
            </section>

            <div className="flex justify-end gap-4">
              <Link to="/dashboard" className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50">
                Cancel
              </Link>
              <button
                type="submit"
                disabled={loading || !formData.disclaimerAccepted}
                className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Generating...
                  </>
                ) : (
                  <>Generate Report</>
                )}
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

export default ReportForm;
